// In-app notification store — persistent (localStorage), reactive across tabs & components.
// Notifications come from: WebSocket order updates, wallet updates, driver alerts, and admin actions.
import { useCallback, useEffect, useState } from "react";

const KEY_PREFIX = "talabya_notif_center_v1:";
const MAX = 20;

function readAll(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(KEY_PREFIX + userId);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function writeAll(userId, arr) {
  if (!userId) return;
  try {
    localStorage.setItem(KEY_PREFIX + userId, JSON.stringify(arr.slice(0, MAX)));
  } catch { /* ignore */ }
}

// Custom event so multiple components in the same tab stay in sync without a full context.
const EVT = "talabya:notif-center-updated";

export function pushNotification(userId, notif) {
  if (!userId || !notif) return;
  const current = readAll(userId);
  const entry = {
    id: notif.id || `n_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: notif.title || "إشعار",
    body: notif.body || "",
    kind: notif.kind || "info",   // info | order | wallet | success | warning
    url: notif.url || null,       // deep link when clicked
    orderCode: notif.orderCode || null,
    at: notif.at || new Date().toISOString(),
    read: false,
  };
  const next = [entry, ...current].slice(0, MAX);
  writeAll(userId, next);
  window.dispatchEvent(new CustomEvent(EVT, { detail: { userId } }));
}

export function useNotificationCenter(userId) {
  const [items, setItems] = useState(() => readAll(userId));

  useEffect(() => {
    setItems(readAll(userId));
    const onEvt = (e) => {
      if (!e.detail || e.detail.userId === userId) setItems(readAll(userId));
    };
    const onStorage = (e) => {
      if (e.key === KEY_PREFIX + userId) setItems(readAll(userId));
    };
    window.addEventListener(EVT, onEvt);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVT, onEvt);
      window.removeEventListener("storage", onStorage);
    };
  }, [userId]);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = useCallback(() => {
    if (!userId) return;
    const next = readAll(userId).map((n) => ({ ...n, read: true }));
    writeAll(userId, next);
    window.dispatchEvent(new CustomEvent(EVT, { detail: { userId } }));
  }, [userId]);

  const markRead = useCallback((id) => {
    if (!userId) return;
    const next = readAll(userId).map((n) => (n.id === id ? { ...n, read: true } : n));
    writeAll(userId, next);
    window.dispatchEvent(new CustomEvent(EVT, { detail: { userId } }));
  }, [userId]);

  const clearAll = useCallback(() => {
    if (!userId) return;
    writeAll(userId, []);
    window.dispatchEvent(new CustomEvent(EVT, { detail: { userId } }));
  }, [userId]);

  return { items, unread, markAllRead, markRead, clearAll };
}
